import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function AdminResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [form, setForm] = useState({ next: "", confirm: "" });
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.next !== form.confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.next });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="reset-password-page" className="min-h-screen bg-[#0B1120] flex items-center justify-center px-6">
      <form
        data-testid="reset-password-form"
        onSubmit={submit}
        className="w-full max-w-sm bg-[#0F172A] border border-white/10 p-10"
      >
        <p className="font-display font-semibold text-white text-xl mb-2">Choose a new password</p>
        {!token && (
          <p data-testid="reset-no-token" className="text-xs text-[#ED1C24] mb-6">
            This reset link is missing its token. Please use the link from your email.
          </p>
        )}
        {done ? (
          <>
            <p data-testid="reset-success" className="text-sm text-emerald-400 leading-relaxed">
              Your password has been changed. You can sign in with it now.
            </p>
            <Link
              to="/admin/login"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] text-white font-medium py-3.5 transition-colors duration-300"
            >
              Go to sign in
            </Link>
          </>
        ) : (
          <>
            <div className="space-y-4 mt-6">
              <Input
                data-testid="reset-new-input"
                type="password"
                required
                placeholder="New password (min 8 characters)"
                value={form.next}
                onChange={(e) => setForm({ ...form, next: e.target.value })}
                className="bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none h-12"
              />
              <Input
                data-testid="reset-confirm-input"
                type="password"
                required
                placeholder="Confirm new password"
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                className="bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none h-12"
              />
            </div>
            {error && <p data-testid="reset-error" className="text-[#ED1C24] text-xs mt-4">{error}</p>}
            <button
              data-testid="reset-submit-btn"
              type="submit"
              disabled={loading || !token}
              className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white font-medium py-3.5 transition-colors duration-300"
            >
              <Lock size={15} />
              {loading ? "Saving..." : "Set new password"}
            </button>
          </>
        )}
      </form>
    </main>
  );
}
